<template>
    <div class="absolute right-3 bottom-32 flex flex-col items-center gap-6 z-[49]">
        <div v-if="videoData.is_audition && !isBillerExpired">
            <!-- Released Status -->
            <a
                v-if="isReleased"
                @click.stop="$emit('watchReleasedSceneClick')"
                :href="route(routePrefix + '.scene.show', videoData.scene_slug)"
                target="_blank"
                class="flex flex-col items-center gap-2"
                type="button"
                aria-label="Watch full scene"
            >
                <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-xl">
                    <Play class="w-6 h-6 text-white ml-0.5" fill="white" />
                </div>
                <div class="text-center">
                    <span class="text-emerald-400 text-xs font-bold block">
                        Watch Scene
                    </span>
                    <span class="text-white/60 text-[10px]">
                        {{ videoData.likes }} stars
                    </span>
                </div>
            </a>

            <!-- Production Status -->
            <div v-else-if="isProduction" class="flex flex-col items-center gap-2">
                <div class="relative w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-xl">
                    <Film class="w-7 h-7 text-white" :stroke-width="2.5" />
                    <div class="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-20" />
                </div>
                <span class="text-white/60 text-[10px]">
                    {{ videoData.likes }} stars
                </span>
            </div>

            <!-- Active Status (non-normal states: sponsored/funded) -->
            <button
                v-else-if="!isNormalState"
                @click="handleStarClick"
                :aria-label="starAriaLabel"
                class="flex flex-col items-center gap-1 group"
                type="button"
            >
                <div class="relative w-14 h-14">
                    <!-- User involvement glow - Brand blue -->
                    <div
                        v-if="showSponsoredIndicators"
                        class="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full blur-md opacity-40 animate-pulse"
                    />

                    <!-- Sponsored success glow - Deep Blue -->
                    <div
                        v-if="isFunded && !showSponsoredIndicators"
                        class="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 rounded-full blur-md opacity-35"
                    />

                    <svg
                        class="absolute inset-0 w-full h-full -rotate-90"
                        viewBox="0 0 64 64"
                        aria-hidden="true"
                    >
                        <circle cx="32" cy="32" :r="radius" fill="none" :stroke="ringBgStroke" stroke-width="4" />
                        <circle cx="32" cy="32" :r="radius" fill="none" :stroke="progressStroke" stroke-width="4"
                            :stroke-dasharray="circumference" :stroke-dashoffset="strokeDashoffset"
                            stroke-linecap="round" class="transition-all duration-500" />
                    </svg>

                    <div class="absolute inset-0 flex items-center justify-center">
                        <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                    </div>

                    <div v-if="showSponsoredIndicators"
                        class="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center shadow-lg border border-black/40">
                        <img :src="STAR_ICON" alt="" class="w-3 h-3" />
                    </div>
                    <div v-if="isFunded"
                        class="absolute -top-1 -left-1 w-5 h-5 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center shadow-lg border border-black/40">
                        <Check class="w-3 h-3 text-white" :stroke-width="3" />
                    </div>
                </div>

                <div class="text-center">
                    <template v-if="showSponsoredIndicators && isFunded">
                        <span class="text-white text-xs font-bold block">Sponsored!</span>
                        <span class="text-white text-[11px] inline-flex items-center gap-1 justify-center">
                            You added {{ videoData.stars_given_by_user }}
                            <img :src="STAR_ICON" alt="" class="w-3.5 h-3.5" />
                        </span>
                        <span class="text-white/70 text-[10px] block">{{ videoData.likes }} stars total</span>
                    </template>
                    <template v-else-if="showSponsoredIndicators">
                        <span class="text-white text-xs font-bold inline-flex items-center gap-1 justify-center">
                            You added {{ videoData.stars_given_by_user }}
                            <img :src="STAR_ICON" alt="" class="w-3.5 h-3.5" />
                        </span>
                        <span class="text-white/70 text-[10px] block">{{ videoData.likes }} / {{ videoData.star_goal }} stars</span>
                        <span class="text-white/85 text-[10px] font-semibold inline-flex items-center mt-0.5 group-hover:text-white transition">Give more</span>
                    </template>
                    <template v-else-if="isFunded">
                        <span class="text-white text-xs font-bold block">Sponsored!</span>
                        <span class="text-white/70 text-[10px] block">{{ videoData.likes }} stars total</span>
                    </template>
                </div>
            </button>

            <!-- Normal state: Combo animation CTA -->
            <button
                v-else
                @click="handleStarClick"
                :aria-label="starAriaLabel"
                class="flex flex-col items-center gap-1 group star-anim-breathe-mobile cursor-pointer"
                type="button"
            >
                <span class="text-white text-[11px] font-bold tracking-wide mb-0.5 drop-shadow-lg">
                    Give Stars
                </span>

                <div class="relative w-14 h-14">
                    <!-- Glow Pulse layer -->
                    <div class="absolute -inset-1.5 rounded-full bg-blue-500 blur-lg star-anim-glow" />

                    <!-- Progress ring -->
                    <svg class="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64" aria-hidden="true">
                        <circle cx="32" cy="32" :r="radius" fill="none" :stroke="ringBgStroke" stroke-width="4" />
                        <circle cx="32" cy="32" :r="radius" fill="none" :stroke="progressStroke" stroke-width="4"
                            :stroke-dasharray="circumference" :stroke-dashoffset="strokeDashoffset"
                            stroke-linecap="round" class="transition-all duration-500" />
                    </svg>

                    <!-- Star icon -->
                    <div class="absolute inset-0 flex items-center justify-center">
                        <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                    </div>

                    <!-- Shimmer layer -->
                    <div class="absolute inset-0 rounded-full overflow-hidden">
                        <div class="star-anim-shimmer absolute inset-0" />
                    </div>
                </div>

                <div class="text-center">
                    <span class="text-white/70 text-[10px] block">{{ videoData.likes }} / {{ videoData.star_goal }}</span>
                </div>
            </button>
        </div>

        <!-- Comments button -->
        <button
            v-if="videoData.is_audition && !isBillerExpired"
            @click="handleViewComments"
            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
            type="button"
            aria-label="Comments"
        >
            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                <MessageCircle class="w-5 h-5 text-white" />
            </div>
            <span v-if="commentCount > 0" class="text-white/60 text-[12px] font-medium tracking-wide">{{ commentCount }}</span>
        </button>

        <!-- Pitch button -->
        <Link
            v-if="castingSlug && !isBillerExpired"
            :href="route(routePrefix + '.studio.casting.view', { slug: castingSlug })"
            target="_blank"
            class="group relative flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
            aria-label="View Pitch"
        >
            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                <Clapperboard class="w-5 h-5 text-white" />
            </div>
            <span class="text-white/60 text-[12px] font-medium tracking-wide">Pitch</span>
            <div
                v-if="castingTitle"
                class="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/80 backdrop-blur-sm text-white text-[11px] font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap max-w-[180px] truncate shadow-lg"
            >
                {{ castingTitle }}
            </div>
        </Link>

        <!-- Share button -->
        <button
            v-if="!isBillerExpired"
            data-modal-target="ShareLinkModal"
            data-modal-toggle="ShareLinkModal"
            @click="handleShareClick"
            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
            type="button"
            aria-label="Share"
        >
            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                </svg>
            </div>
            <span class="text-white/60 text-[12px] font-medium tracking-wide">Share</span>
        </button>
    </div>

    <SharePopup />
</template>

<script setup>
import { computed } from 'vue'
import { Check, Clapperboard, Film, MessageCircle, Play } from 'lucide-vue-next'
import SharePopup from "@/Components/Common/SharePopup.vue";
import { trackFathomEvent, getFathomPostPrefix } from "@/Helpers/analytics";
import {Link} from "@inertiajs/vue3";

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'


const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    sponsorCount: {
        type: Number,
        default: 0
    },
    commentCount: {
        type: Number,
        default: 0
    },
    castingSlug: {
        type: String,
        default: null,
    },
    castingTitle: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['starClick', 'watchReleasedSceneClick', 'viewComments'])

const isBillerExpired = computed(() => props.videoData.feed_post_status == 3)

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const handleStarClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Main_Give_Stars_Clicked`)
    emit('starClick')
}

const handleViewComments = () => {
    trackFathomEvent(`${fathomPrefix.value}_Main_Comments_Clicked`)
    emit('viewComments')
}

const handleShareClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Main_Share_Clicked`)
}

const percentage = computed(() => (props.videoData.likes / props.videoData.star_goal) * 100)
const isReleased = computed(() => props.videoData.release_status === 2 && props.videoData.scene_slug)
const isProduction = computed(() => props.videoData.release_status === 1)
const isFunded = computed(() => props.videoData.star_goal_reached)

const showSponsoredIndicators = computed(() => props.isUserLoggedIn && props.videoData.sponsored_by_user)
const isNormalState = computed(() => !showSponsoredIndicators.value && !isFunded.value && !isReleased.value && !isProduction.value)

// Circle progress calculations
const radius = 28
const circumference = 2 * Math.PI * radius

const strokeDashoffset = computed(() => {
    const clampedPercentage = Math.min(percentage.value, 100)
    return circumference - (clampedPercentage / 100) * circumference
})

const starAriaLabel = computed(() => {
    return showSponsoredIndicators.value
        ? `You already added ${props.videoData.stars_given_by_user} stars. Give more.`
        : 'Give stars'
})

const progressStroke = computed(() => {
    return isFunded.value
        ? 'rgba(0, 81, 213, 0.9)' // Deep Blue 90%
        : 'rgba(0, 100, 250, 0.9)' // Brand Blue 90%
})

const ringBgStroke = computed(() => {
    if (isFunded.value) return 'rgba(0, 81, 213, 0.2)' // Deep Blue tint
    if (showSponsoredIndicators.value) return 'rgba(0, 100, 250, 0.2)' // Brand Blue tint
    return 'rgba(255, 255, 255, 0.2)' // White default
})


</script>

<style scoped>
/* === 1. Glow Pulse — pulses then rests ~5s === */
@keyframes glowPulse {
    0%, 100% { opacity: 0.15; transform: scale(0.9); }
    25% { opacity: 0.5; transform: scale(1.2); }
    50% { opacity: 0.15; transform: scale(0.9); }
    /* 50%-100% = idle/rest */
}
.star-anim-glow {
    animation: glowPulse 10s ease-in-out infinite;
}

/* === 2. Shimmer Sweep — sweeps then rests ~5s === */
@keyframes shimmerSweep {
    0% { transform: translateX(-150%) rotate(-45deg); }
    27%, 100% { transform: translateX(250%) rotate(-45deg); }
    /* stays offscreen from 27%-100% = rest */
}
.star-anim-shimmer {
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,255,255,0.25) 40%,
        rgba(255,255,255,0.4) 50%,
        rgba(255,255,255,0.25) 60%,
        transparent 100%
    );
    width: 60%;
    height: 200%;
    top: -50%;
    animation: shimmerSweep 11s ease-in-out infinite;
}

/* === 3. Bounce/Float === */
@keyframes starFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
}
.star-anim-float {
    animation: starFloat 2s ease-in-out infinite;
}

/* === 4. Ring Ping === */
@keyframes ringPing {
    0% { transform: scale(1); opacity: 0.5; }
    100% { transform: scale(1.9); opacity: 0; }
}
.star-anim-ring-ping {
    animation: ringPing 2s ease-out infinite;
}

/* === 5. Breathe — one breath then rests ~5s === */
@keyframes breathe {
    0%, 100% { transform: scale(1); }
    25% { transform: scale(1.06); }
    50% { transform: scale(1); }
    /* 50%-100% = idle/rest */
}
.star-anim-breathe-mobile {
    animation: breathe 10s ease-in-out infinite;
}
</style>
